import { type ComputedRef, type Ref, ref } from "vue";

import DriveItem from "@/models/Documents/DriveItem";

import TrackHelper from "@/helpers/utilities/TrackHelper";
import { i18n } from "@/helpers/utilities/I18nHelper";

import { useSectionsStore } from "@/stores/sections";
import { useUchiStore } from "@/stores/uchi";
import { useTilesStore } from "@/stores/tiles";

interface CombiDriveFolderDef {
  driveId: string;
  itemId: string;
  name?: string;
  behavior?: { level?: number; tabColor?: string; [key: string]: unknown } | string;
  system?: string;
  group?: string;
  identifier?: string;
  nctSystem?: string;
  nctType?: string;
  nctGroup?: string;
  nctIdentifier?: string;
  nctBehavior?: unknown;
  nctBehaviour?: unknown;
  type?: string;
  [key: string]: unknown;
}

interface SharedDriveLike {
  driveId: string;
  itemId: string;
  displayName?: string;
  [key: string]: unknown;
}

interface ControlledAreaMatch {
  system: string;
  group: string;
  type: string;
  identifier: string;
  behavior?: unknown;
}

interface DriveItemsHierarchyComposableDependencies {
  loadDriveItemWithoutDecorationgWithNCTProps: (driveId: string, itemId: string) => Promise<DriveItem>;
  setNCTMetaOnStoreDriveItems: (driveId: string, itemId: string, metaproperties: Record<string, unknown>) => void;
  loadSharedDrives: () => Promise<void>;
  sharedDrives: ComputedRef<SharedDriveLike[]>;
  // Lazy lookups for symbols still defined in index.ts (controlled area not
  // yet extracted as its own composable).
  getLoadControlledAreaDefinitions: () => () => Promise<void>;
  getControlledAreaFromDriveIdAndItemId: () => (driveId: string, itemId: string) => ControlledAreaMatch | null;
}

export const useDriveItemsHierarchyComposable = (dependencies: DriveItemsHierarchyComposableDependencies) => {
  const driveRootMap: Ref<Record<string, Record<string, CombiDriveFolderDef[]>>> = ref({});

  const calculateDriveItemsHierarchy = async (driveId: string, itemId: string): Promise<CombiDriveFolderDef[]> => {
    if (!driveRootMap.value[driveId] || !driveRootMap.value[driveId][itemId]) {
      await dependencies.loadSharedDrives();
      const loadControlledAreaDefinitions = dependencies.getLoadControlledAreaDefinitions();
      await loadControlledAreaDefinitions();

      const hierarchyAbove = await loadParentDriveItemAndHierarchyAbove(driveId, itemId);

      if (hierarchyAbove.length > 1) {
        const nctMetaProperties = {
          nctSystem: hierarchyAbove[0].nctSystem,
          nctType: hierarchyAbove[0].nctType,
          nctGroup: hierarchyAbove[0].nctGroup,
          nctIdentifier: hierarchyAbove[0].nctIdentifier,
          nctBehaviour: hierarchyAbove[0].nctBehaviour
        };
        hierarchyAbove.forEach((entry: CombiDriveFolderDef & Record<string, unknown>) =>
          dependencies.setNCTMetaOnStoreDriveItems(
            entry.driveId,
            entry.itemId,
            nctMetaProperties as Record<string, unknown>
          )
        );
      }

      if (!driveRootMap.value[driveId]) {
        driveRootMap.value[driveId] = {};
      }
      driveRootMap.value[driveId][itemId] = hierarchyAbove;
    }
    return [...driveRootMap.value[driveId][itemId]];
  };

  const loadParentDriveItemAndHierarchyAbove = async (
    driveId: string,
    itemId: string
  ): Promise<CombiDriveFolderDef[]> => {
    if (!driveId || !itemId) {
      return [];
    }
    const driveItem = await dependencies.loadDriveItemWithoutDecorationgWithNCTProps(driveId, itemId);

    const controlledAreaFromDriveIdAndItemId = dependencies.getControlledAreaFromDriveIdAndItemId();
    const combi = controlledAreaFromDriveIdAndItemId(driveItem.parentReference.driveId, driveItem.id);
    if (combi) {
      return [
        {
          driveId: driveItem.parentReference.driveId,
          itemId: driveItem.id,
          nctSystem: combi.system,
          nctGroup: combi.group,
          nctType: combi.type,
          nctIdentifier: combi.identifier,
          nctBehavior: combi.behavior
        }
      ];
    }

    const sectionsStore = useSectionsStore();
    const sectionDrive = sectionsStore.sectionDriveType(driveItem.parentReference.driveId);
    try {
      if (sectionDrive?.id === itemId) {
        const sectionDriveRootItem = await dependencies.loadDriveItemWithoutDecorationgWithNCTProps(
          driveItem.parentReference.driveId,
          driveItem.id
        );
        return [
          {
            driveId: sectionDriveRootItem.parentReference.driveId,
            itemId: sectionDriveRootItem.id,
            nctSystem: "sectionMaterials",
            nctGroup: "sectionMaterials",
            nctType: "sectionMaterials",
            nctIdentifier: "sectionMaterials",
            nctBehavior: "sectionMaterials"
          }
        ];
      }
      const uchiStore = useUchiStore();
      const uchiSharePointTopFolder = uchiStore.getDriveItemId(driveItem.parentReference.driveId, driveItem.id);
      if (uchiSharePointTopFolder) {
        return [
          {
            name: uchiSharePointTopFolder.title,
            driveId: driveItem.parentReference.driveId,
            itemId: driveItem.id,
            nctSystem: "uchi",
            nctGroup: "uchi",
            nctType: "uchi",
            nctIdentifier: "uchi",
            nctBehavior: "uchi"
          }
        ];
      }
    } catch (caughtError) {
      TrackHelper.registerError(
        caughtError,
        "Failed to load parent drive item and hierarchy for driveId " + driveId + " and itemId " + itemId
      );
    }

    const sharePointTopFolder = dependencies.sharedDrives.value.find(
      (sharePointFolder: SharedDriveLike) =>
        sharePointFolder.driveId === driveItem.parentReference.driveId && sharePointFolder.itemId === driveItem.id
    );
    if (sharePointTopFolder) {
      return [
        {
          name: sharePointTopFolder.displayName,
          driveId: driveItem.parentReference.driveId,
          itemId: driveItem.id
        }
      ];
    }

    const tilesStore = useTilesStore();
    await tilesStore.loadTiles();
    const tileWithRoot = (tilesStore.tiles() as Array<{ title: string; uri?: { uri?: string } }>).find(tile =>
      tile.uri?.uri?.includes(`${driveItem.parentReference.driveId}/${driveItem.id}`)
    );
    if (tileWithRoot) {
      return [
        {
          driveId: driveItem.parentReference.driveId,
          itemId: driveItem.id,
          name: i18n.global.te(tileWithRoot.title) ? i18n.global.t(tileWithRoot.title) : tileWithRoot.title
        }
      ];
    }

    const hierarchyAbove = await loadParentDriveItemAndHierarchyAbove(driveId, driveItem.parentReference.id!);
    const driveItemExt = driveItem as DriveItem & {
      system?: string;
      group?: string;
      type?: string;
      identifier?: string;
      behavior?: unknown;
    };
    hierarchyAbove.push({
      name: driveItem.name,
      driveId: driveItem.parentReference.driveId,
      itemId: driveItem.id,
      nctSystem: driveItemExt.system,
      nctGroup: driveItemExt.group,
      nctType: driveItemExt.type,
      nctIdentifier: driveItemExt.identifier,
      nctBehavior: driveItemExt.behavior
    });

    return hierarchyAbove;
  };

  const calculateDriveItemsRoot = async (driveId: string, itemId: string) => {
    const hierarchy = await calculateDriveItemsHierarchy(driveId, itemId);
    if (hierarchy.length === 0) {
      return null;
    }
    return hierarchy[0];
  };

  return {
    driveRootMap,
    calculateDriveItemsHierarchy,
    loadParentDriveItemAndHierarchyAbove,
    calculateDriveItemsRoot
  };
};
